import { useCallback, useEffect, useState } from "react";
import { encryptionKeyApi } from "@services/encryptionKeyApi";
import { errorMessage, useApiResource } from "@hooks/monitoring/useApiResource";
import type { Rotation } from "@models/encryptionKey";

/** How often a running rotation is polled (§19). */
export const ROTATION_POLL_MS = 10_000;

const FINISHED: ReadonlyArray<Rotation["status"]> = ["done", "rolled_back"];

/**
 * The encryption key's status and the rotation it runs, if any. A running
 * rotation is polled every {@link ROTATION_POLL_MS}; a failed one stops polling
 * and waits for Resume or Roll back.
 */
export const useEncryptionKey = (enabled: boolean) => {
  const status = useApiResource(enabled ? "encryption-key:status" : null,
                                (signal) => encryptionKeyApi().status(signal));
  const [rotationId, setRotationId] = useState<string | null>(null);
  const activeId = rotationId ?? status.data?.running?.id ?? null;
  const rotation = useApiResource(activeId ? `encryption-key:rotation:${activeId}` : null,
                                  (signal) => encryptionKeyApi().rotation(activeId!, signal));
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const polling = activeId !== null && rotation.data !== null &&
    rotation.data.status !== "failed" && !FINISHED.includes(rotation.data.status);
  const { reload: reloadRotation } = rotation;
  const { reload: reloadStatus } = status;

  useEffect(() => {
    if (!polling) return;
    const timer = setInterval(reloadRotation, ROTATION_POLL_MS);
    return () => clearInterval(timer);
  }, [polling, reloadRotation]);

  // A rotation that just finished changes the fingerprint and the last rotation.
  const finished = rotation.data !== null && FINISHED.includes(rotation.data.status);
  useEffect(() => {
    if (finished) reloadStatus();
  }, [finished, reloadStatus]);

  const act = useCallback(async (run: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    try {
      await run();
      return true;
    } catch (err) {
      setActionError(errorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const start = useCallback(
    () => act(async () => {
      const { id } = await encryptionKeyApi().start({ confirmPrerequisites: true });
      setRotationId(id);
      reloadStatus();
    }),
    [act, reloadStatus],
  );
  const resume = useCallback(
    () => act(async () => {
      if (activeId) await encryptionKeyApi().resume(activeId);
      reloadRotation();
    }),
    [act, activeId, reloadRotation],
  );
  const rollback = useCallback(
    () => act(async () => {
      if (activeId) await encryptionKeyApi().rollback(activeId);
      reloadRotation();
      reloadStatus();
    }),
    [act, activeId, reloadRotation, reloadStatus],
  );

  return { status, rotation, polling, busy, actionError, start, resume, rollback };
};
